/**
 * API client for backend REST endpoints.
 * Handles authentication via JWT cookies.
 */

import type {
  UserInfo,
  RegisterRequest,
  LoginRequest,
  AuthConfig,
  RobotModuleRequest,
  RobotModuleResponse,
  ApiError,
  AdminUser,
  AdminModule,
  AccessLogResponse,
  ExportListResponse,
  ExportRecordSummary,
  CatalogListResponse,
  CatalogRegisterResponse,
  LocomotionResultListResponse,
  PrintBodySpec,
  PrintBodyResponse,
  PrintedPartSpecResponse,
} from "./types";

const API_BASE = "/api";

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public detail?: string
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

/**
 * Generic fetch wrapper with error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    let detail: string | undefined;
    try {
      const errorData: ApiError = await response.json();
      detail = errorData.detail;
    } catch {
      // Response body is not JSON
    }

    throw new ApiClientError(
      `API error: ${response.status}`,
      response.status,
      detail || response.statusText
    );
  }

  return response.json();
}

/**
 * API client object with all endpoints
 */
export const apiClient = {
  /**
   * Print a body or branch part from a port spec; the new module joins the library.
   */
  printBody: async (spec: PrintBodySpec): Promise<PrintBodyResponse> => {
    return apiFetch("/print-body", {
      method: "POST",
      body: JSON.stringify(spec),
    });
  },

  /** The spec a printed body or branch was made from, to edit it again. */
  printedPartSpec: async (moduleId: string): Promise<PrintedPartSpecResponse> => {
    return apiFetch(`/print-body/${encodeURIComponent(moduleId)}`);
  },

  /**
   * Health check endpoint
   */
  health: async (): Promise<{ status: string; service: string }> => {
    return apiFetch("/health");
  },

  /**
   * Get current user info (returns anonymous if not authenticated)
   */
  getCurrentUser: async (): Promise<UserInfo> => {
    return apiFetch("/me");
  },

  /**
   * Register a new user account
   */
  register: async (data: RegisterRequest): Promise<UserInfo> => {
    return apiFetch("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Login with username/email and password
   */
  login: async (data: LoginRequest): Promise<UserInfo> => {
    return apiFetch("/auth/login", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Logout (clears auth cookie)
   */
  logout: async (): Promise<void> => {
    await apiFetch<{ message: string }>("/auth/logout", {
      method: "POST",
    });
  },

  /**
   * Get OAuth provider config (which providers are enabled)
   */
  getAuthConfig: async (): Promise<AuthConfig> => {
    return apiFetch("/auth/config");
  },

  /**
   * Redirect to GitHub OAuth login
   */
  loginWithGithub: (): void => {
    window.location.href = "/api/auth/github";
  },

  /**
   * Redirect to Google OAuth login
   */
  loginWithGoogle: (): void => {
    window.location.href = "/api/auth/google";
  },

  /**
   * Create a new robot module
   */
  createModule: async (
    module: RobotModuleRequest
  ): Promise<RobotModuleResponse> => {
    return apiFetch("/modules", {
      method: "POST",
      body: JSON.stringify(module),
    });
  },

  /**
   * Get a snapshot of the current assembly config (for undo).
   * Returns the full config JSON that can be passed to sendLoadConfig.
   */
  getSnapshot: async (sessionId?: string): Promise<string> => {
    const params = sessionId ? `?session=${sessionId}` : "";
    const data = await apiFetch<Record<string, unknown>>(`/snapshot${params}`);
    return JSON.stringify(data);
  },

  /**
   * Admin: list all users
   */
  getAdminUsers: async (): Promise<{ users: AdminUser[] }> => {
    return apiFetch("/admin/users");
  },

  /**
   * Admin: list all modules with owner info
   */
  getAdminModules: async (): Promise<{ modules: AdminModule[] }> => {
    return apiFetch("/admin/modules");
  },

  /**
   * Admin: grant or revoke admin privileges
   */
  setAdmin: async (
    username: string,
    revoke: boolean = false
  ): Promise<{ message: string }> => {
    const params = new URLSearchParams({ username });
    if (revoke) params.set("revoke", "true");
    return apiFetch(`/admin/set-admin?${params.toString()}`);
  },

  /**
   * Admin: read the access log, newest entries first
   */
  getAccessLog: async (options: {
    limit?: number;
    offset?: number;
    kind?: "ws" | "http";
    username?: string;
    type?: string;
  } = {}): Promise<AccessLogResponse> => {
    const params = new URLSearchParams();
    if (options.limit !== undefined) params.set("limit", String(options.limit));
    if (options.offset !== undefined) params.set("offset", String(options.offset));
    if (options.kind) params.set("kind", options.kind);
    if (options.username) params.set("username", options.username);
    if (options.type) params.set("type", options.type);
    const query = params.toString();
    return apiFetch(`/admin/access-log${query ? `?${query}` : ""}`);
  },

  /**
   * Admin: list exported robots, newest first
   */
  getExports: async (
    limit: number = 50,
    offset: number = 0
  ): Promise<ExportListResponse> => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    return apiFetch(`/admin/exports?${params.toString()}`);
  },

  /**
   * Admin: one export record including the assembly config it produced
   */
  getExportRecord: async (
    recordId: string
  ): Promise<ExportRecordSummary & { config: Record<string, unknown> }> => {
    return apiFetch(`/admin/exports/${encodeURIComponent(recordId)}`);
  },

  /**
   * URL of an export's preview image (admin only)
   */
  exportThumbnailUrl: (recordId: string): string =>
    `${API_BASE}/admin/exports/${encodeURIComponent(recordId)}/thumbnail`,

  /**
   * URL that downloads an export's config JSON (admin only).
   * The file can be fed straight back into the builder's Load button.
   */
  exportConfigUrl: (recordId: string): string =>
    `${API_BASE}/admin/exports/${encodeURIComponent(recordId)}/config`,

  /**
   * List all published catalog entries, newest first (public).
   */
  getCatalog: async (): Promise<CatalogListResponse> => {
    return apiFetch("/catalog");
  },

  /**
   * Publish the current assembly of a builder session to the catalog.
   */
  registerCatalogEntry: async (
    name: string,
    description: string,
    sessionId: string
  ): Promise<CatalogRegisterResponse> => {
    return apiFetch("/catalog/register", {
      method: "POST",
      body: JSON.stringify({ name, description, session: sessionId }),
    });
  },

  /**
   * Mint a Live AR hand-off ticket for the session, if we own it.
   *
   * The App Clip's browser has no Safari cookies, so an owned session
   * would reject its websocket; the ticket rides the launch URL instead.
   * Resolves null when no ticket is needed (unowned session) or we are
   * not the owner.
   */
  createLiveArTicket: async (sessionId: string): Promise<string | null> => {
    const data = await apiFetch<{ ticket: string | null }>("/live-ar-ticket", {
      method: "POST",
      body: JSON.stringify({ session: sessionId }),
    });
    return data.ticket;
  },

  /**
   * Delete a catalog entry (author or admin only).
   */
  deleteCatalogEntry: async (id: string): Promise<{ success: boolean }> => {
    return apiFetch("/catalog/delete", {
      method: "POST",
      body: JSON.stringify({ id }),
    });
  },

  /**
   * Config JSON of a catalog entry, ready for sendLoadConfig.
   */
  getCatalogConfig: async (id: string): Promise<string> => {
    const data = await apiFetch<Record<string, unknown>>(
      `/catalog/config?id=${encodeURIComponent(id)}`
    );
    return JSON.stringify(data);
  },

  /**
   * URL of a catalog entry's thumbnail image.
   */
  catalogThumbnailUrl: (id: string): string =>
    `${API_BASE}/catalog/thumbnail?id=${encodeURIComponent(id)}`,

  /**
   * Which locomotion training results exist for a catalog entry (or a
   * builder session id).
   */
  getLocomotionResults: async (id: string): Promise<LocomotionResultListResponse> => {
    return apiFetch(`/locomotion/result?id=${encodeURIComponent(id)}`);
  },

  /**
   * URL of one uploaded training result, e.g. "walk.mp4".
   */
  locomotionResultUrl: (id: string, file: string): string =>
    `${API_BASE}/locomotion/result?id=${encodeURIComponent(id)}&file=${encodeURIComponent(file)}`,

  /**
   * Record a page view. Fire-and-forget: nginx serves the SPA itself, so
   * the backend never sees the navigation otherwise.
   */
  recordPageView: (path: string, sessionId?: string): void => {
    void fetch(`${API_BASE}/access`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path, session: sessionId }),
      keepalive: true,
    }).catch(() => {
      // Access logging must never disturb the page.
    });
  },

  /**
   * Report a browser-side error to the access log (a CLIENT_ERROR entry).
   * Fire-and-forget like the page-view beacon: reporting must never throw.
   */
  reportClientError: (error: { message: string; stack?: string }, path: string, sessionId?: string): void => {
    void fetch(`${API_BASE}/access`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path, session: sessionId, error }),
      keepalive: true,
    }).catch(() => {
      // Error reporting must never disturb the page either.
    });
  },
};
